import React, { useContext, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@mui/material';
import { AuthContext } from 'authentication/AuthContext';
import Wizard from 'common/Wizard';
import { DrawerContext } from 'common/drawer/DrawerContext';
import FilterableDataGrid from 'common/templates/FilterableDataGrid';
import { useUserRolePermissions } from 'hooks';
import { DRAWER_TYPES } from 'utils/constants';
import newSelfAuthSteps from '../create-new-steps/Steps';
import {
  openCreateSelfAuthorizationCloseDialog,
  resetWizardData,
} from '../store/createNewSelfAuthorizationSlice';
import SelfAuthorizationsFilters from './SelfAuthorizationsFilters';
import SelfAuthorizationsListView from './SelfAuthorizationsListView';
import { DEFAULT_STATUS_OPTION, DEFAULT_TYPE_OPTION } from './selfAuthFilterConstants';
import useSelfAuthFilters from './useSelfAuthFilters';

const SelfAuthorizationsListViewPage = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { affiliates = [] } = useContext(AuthContext);
  const { currentAffiliate, isProductLoadingManage } = useUserRolePermissions();
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const inventoryOwnerAffiliates = useMemo(
    () => affiliates.filter(affiliate => affiliate?.inventoryOwner),
    [affiliates]
  );
  const selectedAffiliate = useMemo(
    () =>
      inventoryOwnerAffiliates.find(
        affiliate =>
          affiliate?.customerIdName === currentAffiliate?.customerIdName &&
          affiliate?.dataCompanyId === currentAffiliate?.dataCompanyId
      ) ||
      (currentAffiliate?.inventoryOwner ? currentAffiliate : null) ||
      inventoryOwnerAffiliates[0] ||
      null,
    [currentAffiliate, inventoryOwnerAffiliates]
  );

  const {
    appliedFilters,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    setAffiliateFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    setTypeFilter,
  } = useSelfAuthFilters(selectedAffiliate);

  const handleNewAuthorizations = () => {
    setDrawerComponentProps({
      Component: props => (
        <Wizard steps={newSelfAuthSteps} title="New Self Authorizations" {...props} />
      ),
      forceCloseSelector: state => state.loadingManagement.createNewSelfAuth.forceClose,
      holdOpenSelector: state => {
        const { hasUnsavedChanges, submittingBatch } = state.loadingManagement.createNewSelfAuth;
        return hasUnsavedChanges || submittingBatch;
      },
      resetWizard: () => resetWizardData('location'),
      setCurrentDialogOpen: openCreateSelfAuthorizationCloseDialog,
      type: DRAWER_TYPES.createNewSelfAuth,
    });
  };

  return (
    <FilterableDataGrid
      filterContent={
        <SelfAuthorizationsFilters
          affiliateFilter={draftFilters?.affiliate}
          disabled={isExporting}
          locationFilter={draftFilters?.location}
          productFilter={draftFilters?.product}
          setAffiliateFilter={setAffiliateFilter}
          setLocationFilter={setLocationFilter}
          setProductFilter={setProductFilter}
          setStatusFilter={setStatusFilter}
          setTypeFilter={setTypeFilter}
          statusFilter={draftFilters?.status || DEFAULT_STATUS_OPTION}
          typeFilter={draftFilters?.type || DEFAULT_TYPE_OPTION}
        />
      }
      handleReset={handleReset}
      hasChanges={hasChanges}
      headerTitle="Self Authorizations"
      isExporting={isExporting}
      isOnDefaultState={isOnDefaultState}
      onApply={handleApply}
      onSearchChange={setSearch}
      primaryFilterActionButtonLabel="Apply Filters"
      searchValue={draftFilters?.search || ''}
      secondaryFilterActionButtonLabel="Reset Filters"
      titleMenu={
        isProductLoadingManage ? (
          <Button
            disabled={isExporting}
            onClick={handleNewAuthorizations}
            startIcon={<AddRoundedIcon />}
            variant="vertical-with-icon-contained"
          >
            New Authorization
          </Button>
        ) : null
      }
    >
      <SelfAuthorizationsListView
        appliedFilters={appliedFilters}
        isProductLoadingManage={isProductLoadingManage}
        onExportingChange={setIsExporting}
      />
    </FilterableDataGrid>
  );
};

export default SelfAuthorizationsListViewPage;
